import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {TranslateService} from './core/translate/translate.service';
import { fromEvent, Observable, Subscription } from 'rxjs';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import { StorageService } from './core/storage/storage.service';
import { DOCUMENT } from '@angular/common';
import { properties } from './shared/config';
import { UpdateService } from './core/services/update.service';
import {ControlledInputsHubService} from './core/websocket/controlled-inputs-hub.service';
import { AuthFacade } from './stores/auth/facades/auth.facade';
import { CoreFacade } from './stores/core/facades/core.facade';
import { DeviceDetectorService } from './core/services/device-detector.service';
import { NetworkService } from './core/services/network.service';
import { PositionService } from './core/websocket/position.service';
import { NotificationService } from './shared/services/notification.service';
import { MapFacade } from './main/components/map/services/map.facade';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { TaskNotificationsHubService } from './task-notifications/services';
import { SocketsManagerService } from './sockets-manager/services';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _isOnline: boolean;
  title = 'Teagle';
  loggedIn$: Observable<boolean>;
  isHiddenMenu$: Observable<boolean>;
  loader: LoadingBarState;

  get isOnline(): boolean {
    return this._isOnline;
  }
  set isOnline(value: boolean) {
    if (this._isOnline === value) { return; }
    this._isOnline = value;
    this._networkService.setIsOnlineValue(value);
    if (this._isOnline) {
      this._document.body.classList.add('online');
      this._document.body.classList.remove('offline');
    } else if (!this._isOnline) {
      this._document.body.classList.add('offline');
      this._document.body.classList.remove('online');
    }
  }

  get windowWidth() {
    return window.visualViewport.width;
  }

  constructor(
    private _translateService: TranslateService,
    private _controlledInputsHubService: ControlledInputsHubService,
    private _positionService: PositionService,
    private _notificationService: NotificationService,
    private _taskNotificationsHubService: TaskNotificationsHubService,
    private _socketsManagerService: SocketsManagerService,
    private _matIconRegistry: MatIconRegistry,
    private _domSanitizer: DomSanitizer,
    private _storage: StorageService,
    private _sw: UpdateService,
    @Inject(DOCUMENT) private _document: Document,
    private _authFacade: AuthFacade,
    private _coreFacade: CoreFacade,
    private _deviceDetectorService: DeviceDetectorService,
    private _networkService: NetworkService,
    private _mapFacade: MapFacade,
    private _loader: LoadingBarService
  ) {
    this.loader = this._loader.useRef('main');
    this._hasInternetConnection();
    // check the service worker for updates
    this._sw.checkForUpdates();
    this._subscriptions.add(this._deviceDetectorService.isMobile$.subscribe((val) => {
      if (val) {
        this._document.body.classList.add('device-smartphone');
        if (window['isSafari']) {
          this._document.body.classList.add('is-safari');
        } else {
          this._document.body.classList.remove('is-safari');
        }
      } else {
        this._document.body.classList.remove('device-smartphone');
      }
    }));

    const svgGroup = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="25.846" viewBox="0 0 28 25.846"><defs><style>.a{fill:currentColor;}</style></defs><g transform="translate(-15.763 -31.694)"><circle class="a" cx="5.385" cy="5.385" r="5.385" transform="translate(24.379 31.694)"/><circle class="a" cx="5.385" cy="5.385" r="5.385" transform="translate(15.763 46.771)"/><circle class="a" cx="5.385" cy="5.385" r="5.385" transform="translate(32.994 46.771)"/></g></svg>';
    this._matIconRegistry.addSvgIconLiteral(`group`, this._domSanitizer.bypassSecurityTrustHtml(svgGroup));
    this.loggedIn$ = this._authFacade.getLoggedIn$;
    this.isHiddenMenu$ = this._coreFacade.isMenuHidden$;
    this._subscriptions.add(fromEvent(window.visualViewport, 'resize').subscribe((event) => this.onResize(event)));
  }

  ngOnInit(): void {
    this.isHiddenMenu$.subscribe(() => window.dispatchEvent(new Event('resize')));
    this._setHeight(window.visualViewport);
    this._authFacade.getLoggedIn$.subscribe(val => {
      if (!val) {
        this._positionService.disconnect();
        this._notificationService.disconnect();
        this._controlledInputsHubService.disconnect();
        this._taskNotificationsHubService.disconnect();
        this._mapFacade.destroy();
      } else {
        this._mapFacade.init();
      }

      this._socketsManagerService.removeAllErrors();
      this._socketsManagerService.unregisterAllSockets();
    });
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  onResize(event) {
    this._setHeight(event.target);
  }

  private _setHeight(event) {
    let height = event.height;
    const isLoginForm = document.getElementById('login');

    if (window['mobileCheck']() ) {
     if (window['isSafari']) {
       height += event.offsetTop;
     } else {
       height = event.height;
     }
    }

    properties.height = height;
    properties.width = event.width;
  }

  private _hasInternetConnection() {
    this.isOnline = window.navigator.onLine;
    window.addEventListener('online', () => this.isOnline = true);
    window.addEventListener('offline', () => this.isOnline = false);
  }
}
